// libs
import React, { useRef, useState, useMemo, useEffect } from 'react'
import { Box, Grid } from '@material-ui/core'
import { useFormik } from 'formik'

// pendency status
import SelectPendencyStatus from '../SelectPendencyStatus'
import {
  DoctorRegister,
  DoctorRegisterStepStatus
} from 'domain/entities/doctor-register'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'

// components
import TextArea from 'presentation/shared/components/TextArea'
import Button from 'presentation/shared/components/Button'
import Modal from 'presentation/shared/components/Modal'

// assets
import * as S from './styles'
import ModalImage from 'presentation/assets/icons/doctor-confirmation.png'

import {
  FileDeleteEvent,
  FileUploadEvent,
  FormDocument,
  FormDocumentEvent,
  FormDocumentProps
} from './FormDocument'
import FileViewer from 'presentation/shared/components/FileViewer'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'
import { DoctorDocumentValidation } from 'domain/entities/doctor-document-validation-model'
import { useServices } from 'presentation/hooks/use-services'
import { toast } from 'react-toastify'
import { translateAcademicFormation } from 'presentation/utils/translators/academic-formation'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'

interface SourceValidationFormType {
  [DoctorRegistrationSteps.SOURCE_VALIDATION]: {
    status: DoctorRegisterStepStatus
    director_review?: string
  }
}

type SectionType = Omit<FormDocumentProps, 'onChange'>

interface SourceValidationProps {
  initialValues: SourceValidationFormType
  isDirector: boolean
  documentValidations?: DoctorDocumentValidation[]
  doctorRegister?: DoctorRegister
  updateStepStatus?: (step: any) => Promise<void>
  graduation?: string
  primarySpeciality?: string
  secondarySpecialities?: string[]
  onPageLoad?: () => void
}
function SourceValidation({
  initialValues,
  isDirector,
  documentValidations = [],
  doctorRegister,
  updateStepStatus,
  graduation,
  primarySpeciality,
  secondarySpecialities = [],
  onPageLoad
}: SourceValidationProps) {
  const { isStepsUpdateBlocked } = newRegistrationDetailsContext

  const doctorDocumentValidationService = useServices().doctorDocumentValidation
  const doctorService = useServices().doctor

  const scrollRef = useRef<HTMLDivElement>(null)

  const documentValidationToSection = (
    documentValidation: DoctorDocumentValidation
  ): Omit<FormDocumentProps, 'onChange'> => {
    const sectionId =
      documentValidation.document_id ?? Math.floor(Math.random() * -100) // NOSONAR

    const { document } = documentValidation
    const file = new File([], document.name)

    return {
      sectionId: `${sectionId}`,
      title: documentValidation.title,
      description: documentValidation.description,
      file: {
        file: file,
        sectionId: String(sectionId),
        uploadedAt: new Date(document.updatedAt ?? ''),
        isEditable: true,
        fileId: String(document.document_id)
      },
      doctor_document_validation_id:
        documentValidation.doctor_document_validation_id,
      document_id: document.document_id,
      isEditable: true,
      canDelete: true,
      isTitleEditable: true
    }
  }

  const resolvedInitialScreens = useMemo<SectionType[]>(() => {
    const traslatedGraduation = translateAcademicFormation(graduation || '')
    const graduationSection: SectionType = {
      sectionId: '1000',
      canDelete: false,
      isTitleEditable: false,
      isEditable: true,
      title: `Diploma de ${traslatedGraduation}`
    }

    const primarySpecialitySection: SectionType = {
      sectionId: '2000',
      canDelete: false,
      isTitleEditable: false,
      isEditable: true,
      title: primarySpeciality || 'Especialidade principal'
    }

    const secondarySpecialitiesSections: SectionType[] =
      secondarySpecialities.map((item, key) => ({
        sectionId: String((key + 3) * 1000),
        canDelete: false,
        isTitleEditable: false,
        isEditable: true,
        title: item
      }))

    return [
      graduationSection,
      primarySpecialitySection,
      ...secondarySpecialitiesSections
    ]
  }, [graduation, primarySpeciality, secondarySpecialities])

  const getInitialSections = (): SectionType[] => {
    const sections = [...resolvedInitialScreens]

    documentValidations!.map(documentValidationToSection).forEach((section) => {
      const defaultSection = sections.find((s) => s.title === section.title)
      if (defaultSection) {
        defaultSection.doctor_document_validation_id =
          section.doctor_document_validation_id
        defaultSection.description = section.description
        defaultSection.file = section.file
        defaultSection.document_id = section.document_id
      } else {
        sections.unshift(section)
      }
    })

    return sections
  }

  const [sections, setSections] = useState<SectionType[]>([])

  useEffect(() => {
    setSections(getInitialSections())
  }, [documentValidations])

  const { values, handleSubmit, setFieldValue, handleChange } = useFormik({
    initialValues,
    async onSubmit(values) {
      try {
        await updateStepStatus?.(values)
        toast.success('Dados enviados com sucesso.')
        window.scrollTo(0, 0)
      } catch (error) {
        toast.error('Erro ao salvar dados.')
      }
    }
  })

  const handleAdd = () => {
    const currentState = [...sections]

    const newSection: Omit<FormDocumentProps, 'onChange'> = {
      sectionId: String(Math.round(Math.random() * (1000 - 1) + 1)), // NOSONAR
      canDelete: true,
      isTitleEditable: true,
      isEditable: true,
      title: '',
      description: ''
    }

    if (scrollRef.current) {
      scrollRef.current.scroll({
        left: 0,
        behavior: 'smooth'
      })
    }

    setSections([newSection, ...currentState])
  }

  const handleChangeSection = (event: FormDocumentEvent) => {
    const currentState = [...sections]

    const found = currentState.findIndex(
      (item) => item.sectionId === event.sectionId
    )

    if (found > -1) {
      const item = currentState[found]

      currentState.splice(found, 1, {
        ...item,
        ...event
      })

      setSections(currentState)
    }
  }

  const handleUploadFile = (evt: FileUploadEvent) => {
    const currentState = [...sections]

    const index = currentState.findIndex(
      (item) => item.sectionId === evt.sectionId
    )

    if (index === -1) return

    const item = currentState[index]

    currentState.splice(index, 1, {
      ...item,
      document_id: undefined,
      file: {
        file: evt.file,
        uploadedAt: new Date(),
        isEditable: true,
        sectionId: item.sectionId
      }
    })
    setSections(currentState)
  }

  const handleDeleteFile = (event: FileDeleteEvent) => {
    const currentState = [...sections]

    const index = currentState.findIndex((item) => {
      return item.sectionId === event.sectionId
    })

    if (index === -1) return

    const item = currentState[index]

    currentState.splice(index, 1, { ...item, file: undefined })

    setSections(currentState)
  }

  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [fileContentType, setContentType] = useState<string | null>(null)

  const loadRemoteFile = async (
    section: Omit<FormDocumentProps, 'onChange'>
  ) => {
    const { document_id } = section

    if (!doctorRegister?.doctor_id || !document_id) return

    try {
      const doctorDocument = await doctorService.loadDoctorDocument({
        doctor_id: doctorRegister.doctor_id,
        document_id: document_id
      })
      if (!doctorDocument) return
      setFileUrl(URL.createObjectURL(doctorDocument.data))
      setContentType(doctorDocument.data.type)
    } catch {
      toast.error('Erro ao carregar arquivo')
    }
  }

  const loadLocalFile = (section: Omit<FormDocumentProps, 'onChange'>) => {
    const file = section?.file?.file

    if (!file) return

    setFileUrl(URL.createObjectURL(file))
    setContentType(file.type)
  }

  const handleOpenFileViewer = async (sectionId: string) => {
    const section = sections.find((item) => item.sectionId === sectionId)

    if (!section) return

    if (section.document_id) {
      loadRemoteFile(section)
    } else {
      loadLocalFile(section)
    }
  }

  const handleDownloadFile = async (sectionId: string) => {
    const section = sections.find((item) => item.sectionId === sectionId)

    if (!section) return

    if (section.document_id) {
      const doctorDocument = await doctorService.loadDoctorDocument({
        doctor_id: doctorRegister!.doctor_id!,
        document_id: section.document_id
      })
      downloadFileFromBlob(
        doctorDocument.data,
        doctorDocument.contentType,
        section.file?.file.name ?? ''
      )
    } else if (section.file?.file) {
      downloadFileFromBlob(
        section.file?.file,
        section.file?.file.type,
        section.file?.file.name ?? ''
      )
    }
  }

  const handleResetFileViewer = () => {
    setFileUrl(null)
    setContentType(null)
  }

  const [sectionIdPrepare, setSectionIdPrepare] = useState<string | null>(null)

  const deleteSection = async (sectionId: string) => {
    if (!sectionId) return
    const currentState = [...sections]

    const index = currentState.findIndex((item) => {
      return item.sectionId === sectionId
    })

    if (index === -1) return
    const section = currentState[index]
    try {
      if (section.doctor_document_validation_id) {
        await doctorDocumentValidationService.deleteDoctorDocumentValidation({
          doctor_document_validation_id: section.doctor_document_validation_id
        })
      }
      currentState.splice(index, 1)
      setSections(currentState)
      setSectionIdPrepare(null)
      toast.success('Validação da fonte removida com sucesso!')
    } catch {
      toast.error('Erro ao tentar remover validação da fonte')
    }
  }
  const dispatchDeleteEvent = () => {
    if (!sectionIdPrepare) return

    deleteSection(sectionIdPrepare)
  }
  const handleDeleteSection = (sectionId: string) => {
    const found = sections.find((item) => item.sectionId === sectionId)

    if (found && !found.file) return deleteSection(sectionId)

    setSectionIdPrepare(sectionId)
  }

  const setPendencyStatus = (field: string, event: any) => {
    if (isDirector) {
      setFieldValue(
        `${field}.status`,
        event.target.value === 'Revisão'
          ? DoctorRegisterStepStatus.REVIEW
          : DoctorRegisterStepStatus.APPROVED_DIRECTOR
      )
    } else {
      setFieldValue(
        `${field}.status`,
        event.target.value === 'Pendente'
          ? DoctorRegisterStepStatus.PENDENT
          : DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER
      )
    }
  }

  const createDocumentValidation = async (
    section: Omit<FormDocumentProps, 'onChange'>
  ) => {
    const { file, description, title } = section

    if (!(file?.file && description && title && doctorRegister)) return

    try {
      const { doctor_document_validation_id } =
        await doctorDocumentValidationService.createDoctorDocumentValidation({
          title,
          description,
          file: file?.file,
          doctor_register_id: doctorRegister.doctor_register_id!
        })

      section.doctor_document_validation_id = doctor_document_validation_id

      const index = sections.findIndex((item) => {
        return item.sectionId === section.sectionId
      })

      if (index === -1) return

      const newSections = [...sections]
      newSections.splice(index, 1, section)
      setSections(newSections)
      toast.success('Validação da fonte salva com sucesso')
    } catch {
      toast.error('Erro ao salvar validação da fonte')
    }
  }

  const updateDocumentValidation = async (
    section: Omit<FormDocumentProps, 'onChange'>
  ) => {
    const {
      file,
      description,
      title,
      doctor_document_validation_id,
      document_id
    } = section

    if (
      !description ||
      !title ||
      !doctorRegister ||
      !doctor_document_validation_id
    )
      return

    try {
      await doctorDocumentValidationService.updateDoctorDocumentValidation({
        title,
        description,
        file: !document_id ? file?.file : undefined,
        doctor_register_id: doctorRegister.doctor_register_id!,
        doctor_document_validation_id
      })
      toast.success('Validação da fonte atualizada com sucesso')
    } catch (error) {
      toast.error('Erro ao atualizar validação da fonte')
    }
  }

  const handleSaveSection = async (
    section: Omit<FormDocumentProps, 'onChange'>
  ) => {
    if (section.doctor_document_validation_id) {
      await updateDocumentValidation(section)
    } else {
      await createDocumentValidation(section)
    }
  }

  useEffect(() => {
    if (onPageLoad) onPageLoad()
  }, [])

  return (
    <Box mt="12px">
      <Grid container>
        <Grid item xs={12}>
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            mt="12px"
          >
            {!isDirector && (
              <Box mr={2}>
                <S.AddText onClick={handleAdd}>Adicionar evidência+</S.AddText>
              </Box>
            )}
            <SelectPendencyStatus
              options={isDirector ? undefined : ['Aprovado']}
              handleChange={(e) =>
                setPendencyStatus(DoctorRegistrationSteps.SOURCE_VALIDATION, e)
              }
              initialValue={
                values[DoctorRegistrationSteps.SOURCE_VALIDATION].status
              }
              disabled={isStepsUpdateBlocked}
              isDirector={isDirector}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <S.Title>Validação da fonte</S.Title>
        </Grid>
        <Grid item xs={12}>
          <S.ScrollContainer>
            {sections.map((item, key) => (
              <Box
                key={item.sectionId}
                mr={2}
                ml={key > 0 ? 2 : 1}
                mt={1}
                mb={1}
              >
                <FormDocument
                  canDelete={item.canDelete && !isDirector}
                  sectionId={item.sectionId}
                  description={item.description}
                  file={item.file}
                  isEditable={item.isEditable}
                  isTitleEditable={item.isTitleEditable}
                  title={item.title}
                  onChange={handleChangeSection}
                  onDelete={handleDeleteSection}
                  onFileUpload={handleUploadFile}
                  onFileDelete={handleDeleteFile}
                  onFileView={handleOpenFileViewer}
                  onFileDownload={handleDownloadFile}
                  onSave={() => handleSaveSection(item)}
                />
              </Box>
            ))}
          </S.ScrollContainer>
        </Grid>
        <Grid item xs={12} style={{ marginTop: '16px' }}>
          <TextArea
            rows={6}
            label="Observações do diretor"
            required
            name={`${DoctorRegistrationSteps.SOURCE_VALIDATION}.director_review`}
            value={
              values[DoctorRegistrationSteps.SOURCE_VALIDATION].director_review
            }
            onChange={handleChange}
            disabled={
              !isDirector ||
              values[DoctorRegistrationSteps.SOURCE_VALIDATION].status !==
                DoctorRegisterStepStatus.REVIEW
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <S.ActionsButtons>
          <Button
            data-testid="btn-save-source-validation"
            onClick={() => handleSubmit()}
            variant={!isStepsUpdateBlocked ? 'outlined' : undefined}
            disabled={isStepsUpdateBlocked}
          >
            Salvar
          </Button>
        </S.ActionsButtons>
      </Grid>
      {fileUrl && fileContentType ? (
        <FileViewer
          file={{
            fileUrl: fileUrl,
            contentType: fileContentType
          }}
          onClose={handleResetFileViewer}
          show
        />
      ) : null}
      <Modal
        data-testid="modal-confirm-generate-pendency"
        show={Boolean(sectionIdPrepare)}
        style={{ width: '554px', height: 'auto' }}
        preventAutomateClose
        preventPadding
      >
        <Box p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <img
                style={{ height: '165px' }}
                src={ModalImage}
                alt={'Ilustração'}
              />
            </Grid>
            <Grid item xs={12}>
              <S.ModalTitle>
                Tem certeza que deseja excluir a seção?
              </S.ModalTitle>
            </Grid>
            <Grid item xs={12}>
              <S.ModalSubtitle>A ação não poderá ser desfeita!</S.ModalSubtitle>
            </Grid>
            <Grid item xs={12}>
              <S.Buttons>
                <Button
                  data-testid="btn-cancel-confirm-generate-pendency"
                  variant="outlined"
                  type="button"
                  onClick={() => setSectionIdPrepare(null)}
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="btn-confirm-generate-pendency"
                  type="button"
                  onClick={dispatchDeleteEvent}
                >
                  Excluir
                </Button>
              </S.Buttons>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Box>
  )
}

export default SourceValidation
